import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Lock, Eye, EyeOff, Loader2 } from "lucide-react";

export default function ChangePassword() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { clearRecoverySession } = useAuth();
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [isProcessingToken, setIsProcessingToken] = useState(true);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    let sessionEstablished = false;

    // Listen for PASSWORD_RECOVERY event FIRST - Supabase may process the token automatically
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      
      console.log('ChangePassword: Auth event:', event, 'Has session:', !!session);
      
      if (event === 'PASSWORD_RECOVERY' && session) {
        console.log('ChangePassword: PASSWORD_RECOVERY event received');
        sessionEstablished = true;
        setIsRecoveryMode(true);
        setIsProcessingToken(false);
        // Clear URL params
        window.history.replaceState({}, '', window.location.pathname);
      }
    });

    const processAuthTokens = async () => {
      // Wait a bit to let onAuthStateChange fire first if Supabase processes the token
      await new Promise(r => setTimeout(r, 500));
      
      if (!mounted) return;
      
      // If session was already established by onAuthStateChange, we're done
      if (sessionEstablished) {
        console.log('ChangePassword: Session already established via onAuthStateChange');
        return;
      }

      // Check if user already has a valid session (might have been set by Supabase automatically)
      const { data: { session: existingSession } } = await supabase.auth.getSession();
      if (existingSession) {
        console.log('ChangePassword: Existing session found');
        setIsRecoveryMode(true);
        setIsProcessingToken(false);
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      // Parse both query params and hash params
      const queryParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(
        window.location.hash.startsWith('#') 
          ? window.location.hash.slice(1) 
          : window.location.hash
      );
      
      const getParam = (key: string) => queryParams.get(key) ?? hashParams.get(key);

      const code = getParam('code');
      const accessToken = getParam('access_token');
      const refreshToken = getParam('refresh_token');
      const tokenHash = getParam('token_hash');
      const type = getParam('type');

      const hasTokens = code || accessToken || tokenHash;

      console.log('ChangePassword: Processing auth params', { 
        hasCode: !!code, 
        hasAccessToken: !!accessToken,
        hasTokenHash: !!tokenHash,
        type 
      });

      if (!hasTokens) {
        console.log('ChangePassword: No tokens found in URL');
        setIsProcessingToken(false);
        navigate('/auth');
        return;
      }

      try {
        let error: Error | null = null;

        if (code) {
          console.log('ChangePassword: Exchanging code for session');
          const result = await supabase.auth.exchangeCodeForSession(code);
          error = result.error;
        } else if (accessToken && refreshToken) {
          console.log('ChangePassword: Setting session from tokens');
          const result = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          error = result.error;
        } else if (tokenHash && type === 'recovery') {
          console.log('ChangePassword: Verifying OTP token');
          const result = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });
          error = result.error;
        }

        if (error) {
          console.error('ChangePassword: Token exchange error:', error);
          
          // Check again if session was established despite error (race condition)
          const { data: { session: checkSession } } = await supabase.auth.getSession();
          if (checkSession) {
            console.log('ChangePassword: Session exists despite error, proceeding');
            setIsRecoveryMode(true);
            setIsProcessingToken(false);
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }
          
          toast({
            title: "Link expired or invalid",
            description: "Please request a new password reset link.",
            variant: "destructive",
          });
          navigate('/auth');
          return;
        }

        console.log('ChangePassword: Token exchange successful');
        setIsRecoveryMode(true);
        window.history.replaceState({}, '', window.location.pathname);
      } catch (err) {
        console.error('ChangePassword: Unexpected error:', err);
        
        // Final check - maybe session was established anyway
        const { data: { session: finalCheck } } = await supabase.auth.getSession();
        if (finalCheck) {
          console.log('ChangePassword: Session found after error, proceeding');
          setIsRecoveryMode(true);
          setIsProcessingToken(false);
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
        
        toast({
          title: "Error",
          description: "An error occurred. Please try again.",
          variant: "destructive",
        });
        navigate('/auth');
      } finally {
        if (mounted) {
          setIsProcessingToken(false);
        }
      }
    };

    processAuthTokens();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, toast]);

  const validatePassword = (pwd: string) => {
    if (pwd.length < 8) return "Password must be at least 8 characters long";
    if (!/(?=.*[a-z])/.test(pwd)) return "Password must contain at least one lowercase letter";
    if (!/(?=.*[A-Z])/.test(pwd)) return "Password must contain at least one uppercase letter";
    if (!/(?=.*\d)/.test(pwd)) return "Password must contain at least one number";
    return null;
  };

  const handleChangePassword = async () => {
    const passwordError = validatePassword(password);
    if (passwordError) {
      toast({
        title: "Invalid password",
        description: passwordError,
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are identical",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      toast({
        title: "Password updated successfully",
        description: isRecoveryMode 
          ? "Your password has been reset. You can now sign in with your new password."
          : "Your password has been changed successfully.",
      });

      // Clear ALL recovery session indicators
      clearRecoverySession();
      localStorage.removeItem('recovery_session_active');

      // Sign out ALL sessions globally - forces re-login on all devices/tabs
      await supabase.auth.signOut({ scope: 'global' });
      navigate('/auth');

    } catch (error: any) {
      console.error('Password change error:', error);
      toast({
        title: "Failed to change password",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Show loading while processing tokens
  if (isProcessingToken) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Processing your request...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <main className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">
              {isRecoveryMode ? "Reset Password" : "Change Password"}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {isRecoveryMode 
                ? "Enter your new password below."
                : "Update your current password to a new secure password."}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your new password"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>Password requirements:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>At least 8 characters long</li>
                <li>One uppercase letter</li>
                <li>One lowercase letter</li>
                <li>One number</li>
              </ul>
            </div>

            <Button 
              className="w-full" 
              onClick={handleChangePassword} 
              disabled={loading || !password || !confirmPassword}
            >
              {loading ? "Updating Password..." : "Update Password"}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
